import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface OperacaoFluxoCaixa {
  id: number;
  descricao: string;
  observacao?: string | null;
  contaContabilDebitoId: number;
  contaContabilCreditoId: number;
  isAtivo: boolean;
}

export interface SalvarOperacaoFluxoCaixaRequest {
  descricao: string;
  contaContabilDebitoId: number;
  contaContabilCreditoId: number;
  observacao?: string | null;
  isAtivo: boolean;
}

interface Paginado<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

const BASE = "/api/cadastros/operacoes-fluxo-caixa";

export function useOperacoesFluxoCaixa(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["operacoes-fluxo-caixa"] });

  const query = useQuery({
    queryKey: ["operacoes-fluxo-caixa", busca, pagina, tamanhoPagina],
    queryFn: async () => {
      const { data } = await apiClient.get<Paginado<OperacaoFluxoCaixa>>(BASE, {
        params: { busca: busca || undefined, pagina, tamanhoPagina },
      });
      return data;
    },
  });

  const criar = useMutation({
    mutationFn: async (req: SalvarOperacaoFluxoCaixaRequest) => {
      const { data } = await apiClient.post<OperacaoFluxoCaixa>(BASE, req);
      return data;
    },
    onSuccess: () => { invalidate(); toast.success("Operação criada."); },
    onError: (e: Error) => toast.error("Erro ao criar operação: " + e.message),
  });

  const atualizar = useMutation({
    mutationFn: async ({ id, req }: { id: number; req: SalvarOperacaoFluxoCaixaRequest }) => {
      await apiClient.put(`${BASE}/${id}`, req);
    },
    onSuccess: () => { invalidate(); toast.success("Operação atualizada."); },
    onError: (e: Error) => toast.error("Erro ao atualizar operação: " + e.message),
  });

  const excluir = useMutation({
    mutationFn: async (id: number) => { await apiClient.delete(`${BASE}/${id}`); },
    onSuccess: () => { invalidate(); toast.success("Operação excluída."); },
    onError: (e: Error) => toast.error("Erro ao excluir operação: " + e.message),
  });

  return { query, criar, atualizar, excluir };
}
